import React, { useState, useEffect } from 'react'
import { Star, Quote } from 'lucide-react'
import { supabase } from '../services/supabase'

interface Testimonial {
  id: number
  name: string
  message: string
  rating: number
  created_at: string
}

// Fallback testimonials if API fails
const fallbackTestimonials = [
  {
    id: 1,
    name: "Sarah Johnson",
    message: "This tool has completely changed how I make decisions. The AI analysis is incredibly insightful and helps me see angles I never considered.",
    rating: 5,
    created_at: "2024-01-15T10:30:00Z"
  },
  {
    id: 2,
    name: "Mike Chen",
    message: "Perfect for quick decisions when I'm stuck. The spinning wheel is fun and the AI analysis gives me confidence in my choices.",
    rating: 5,
    created_at: "2024-01-14T15:45:00Z"
  },
  {
    id: 3,
    name: "Emily Rodriguez",
    message: "I use this in my classroom for group activities. The kids love the spinning wheel and it makes decision-making fair and fun.",
    rating: 5,
    created_at: "2024-01-13T09:20:00Z"
  },
  {
    id: 4,
    name: "David Thompson",
    message: "The causal chain analysis is brilliant. It helps me explain complex decisions to clients in a clear, visual way.",
    rating: 5,
    created_at: "2024-01-12T14:15:00Z"
  },
  {
    id: 5,
    name: "Lisa Wang",
    message: "As a student, I use this for everything from choosing study topics to deciding what to eat. It's become my go-to decision helper.",
    rating: 5,
    created_at: "2024-01-11T11:30:00Z"
  },
  {
    id: 6,
    name: "Alex Martinez",
    message: "Great for team decisions and project prioritization. The multi-option analysis helps us make data-driven choices quickly.",
    rating: 5,
    created_at: "2024-01-10T16:45:00Z"
  }
]

export const Testimonials: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>(fallbackTestimonials)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const { data, error } = await supabase
          .from('contact_submissions')
          .select('id, name, message, rating, created_at')
          .eq('is_public', true)
          .eq('status', 'resolved')
          .order('created_at', { ascending: false })
          .limit(10)

        if (error) {
          console.error('Database error:', error)
          // Keep fallback testimonials - this is expected if table doesn't exist yet
        } else if (data && data.length > 0) {
          setTestimonials(data)
        }
      } catch (error) {
        console.error('Failed to fetch testimonials:', error)
        // Keep fallback testimonials
      } finally {
        setIsLoading(false)
      }
    }

    fetchTestimonials()
  }, [])

  // Generate avatar emoji based on name
  const getAvatar = (name: string) => {
    const emojis = ['👩‍💼', '👨‍💻', '👩‍🏫', '👨‍💼', '👩‍🎓', '👨‍💼', '👩‍🔬', '👨‍🔬']
    const index = name.charCodeAt(0) % emojis.length
    return emojis[index]
  }

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  return (
    <section className="mb-16">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Users Say</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Join thousands of satisfied users who are making better decisions with AI Decision Maker.
        </p>
      </div>
      
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-lg p-6 border animate-pulse">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gray-200 rounded-full mr-3"></div>
                <div>
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded"></div>
                <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                <div className="h-3 bg-gray-200 rounded w-4/6"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="bg-white rounded-xl shadow-lg p-6 border hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <div className="text-3xl mr-3">{getAvatar(testimonial.name)}</div>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{formatDate(testimonial.created_at)}</div>
                </div>
              </div>
              
              <div className="flex items-center mb-3">
                {Array.from({ length: testimonial.rating }).map((_, index) => (
                  <Star key={index} className="w-4 h-4 text-yellow-400 fill-current" />
                ))}
              </div>
              
              <div className="relative">
                <Quote className="absolute -top-2 -left-2 w-6 h-6 text-gray-200" />
                <p className="text-gray-600 text-sm leading-relaxed pl-4">
                  "{testimonial.message}"
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="text-center mt-8">
        <div className="inline-flex items-center space-x-2 bg-green-50 text-green-700 px-4 py-2 rounded-full">
          <Star className="w-4 h-4 fill-current" />
          <span className="font-medium">4.9/5 Average Rating</span>
        </div>
      </div>
    </section>
  )
} 